import { create } from 'zustand'
import {
  OnboardingQuestion,
  OnboardingResponse,
} from '../common/types/onboarding-questions'
import { authFetch } from '../common/utils/cognito'
import { Track } from '../common/types/track'

interface OnboardingStore {
  questions: Array<OnboardingQuestion>
  responses: Array<OnboardingResponse>
  recommendations: Array<number>
  highRisk: boolean
  selectedTracks: Array<number>
  tracks: Array<Track>
  hasStarted: boolean
  fetchQuestions: () => Promise<void>
  fetchTracks: () => Promise<void>
  fetchSelectedTracks: () => Promise<void>
  getAnswer: (questionId: number) => number | boolean | undefined
  setAnswer: (questionId: number, answer: number | boolean) => void
  setRecommendations: (recommendations: Array<number>) => void
  setHighRisk: (highRisk: boolean) => void
  setSelectedTracks: (recommendations: Array<number>) => void
}

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  questions: [],
  responses: [],
  recommendations: [],
  highRisk: true,
  selectedTracks: [],
  tracks: [],
  hasStarted: false,
  fetchQuestions: async () => {
    const questions = await fetchQuestions()
    set({ questions })

    try {
      const responses = await fetchResponses()
      set({ responses, hasStarted: true })
    } catch (error) {
      set({ hasStarted: false })
    }
  },
  fetchTracks: async () => {
    const tracks = await fetchTracks()
    set({ tracks })
  },
  fetchSelectedTracks: async () => {
    const response = await authFetch(`${process.env.REACT_APP_API_URL}/me`)

    if (response.ok) {
      const { tracks } = await response.json()
      const selectedTracks = tracks.map((track: Track) => track.id)
      set({ selectedTracks })
    } else {
      throw new Error(response.statusText)
    }
  },
  getAnswer: (questionId: number) => {
    const responses = get().responses
    return responses.find(({ question }) => question.id === questionId)?.answer
  },
  setAnswer: (questionId: number, answer: number | boolean) => {
    const questions = get().questions
    const question = questions.find(({ id }) => id === questionId)

    const responses = get().responses
    const responseIndex = responses.findIndex(
      ({ question }) => question.id === questionId,
    )

    if (responseIndex !== -1) {
      responses[responseIndex].answer = answer
      set({ responses })
    } else if (question !== undefined) {
      set({
        responses: [
          ...responses,
          {
            answer,
            question,
          },
        ],
      })
    }
  },
  setRecommendations: (recommendations: Array<number>) => {
    set({ recommendations })
  },
  setHighRisk: (highRisk: boolean) => {
    set({ highRisk })
  },
  setSelectedTracks: (selectedTracks: Array<number>) => {
    set({ selectedTracks })
  },
}))

const fetchQuestions = async (): Promise<Array<OnboardingQuestion>> => {
  const response = await authFetch(
    `${process.env.REACT_APP_API_URL}/user-onboarding/questions`,
  )

  if (response.ok) {
    return await response.json()
  } else {
    throw new Error(response.statusText)
  }
}

const fetchResponses = async (): Promise<Array<OnboardingResponse>> => {
  const response = await authFetch(
    `${process.env.REACT_APP_API_URL}/user-onboarding`,
  )

  if (response.ok) {
    const json = await response.json()
    return json.responses
  } else {
    throw new Error(response.statusText)
  }
}

const fetchTracks = async (): Promise<Array<Track>> => {
  const response = await authFetch(
    `${process.env.REACT_APP_API_URL}/user-onboarding/tracks`,
  )

  if (response.ok) {
    return await response.json()
  } else {
    throw new Error(response.statusText)
  }
}
